/**
 * NotesPage — My Notes list (My Notes rebuild, Stage 6)
 *
 * Title-first rows, each clickable through to its own page
 * (NoteDetail.jsx). The composer is two steps — title, then body +
 * priority — per Jasper's spec, reusing the exact focus-ring input
 * treatment the old single-textarea version already had (it was already
 * the nicest input in the app). StickyAskBar replaces the old
 * JaiSuggestionStrip — one way to ask JAI about these notes, not two.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useLeagues } from '../context/LeagueContext'
import AppShell from '../components/AppShell'
import ToolHeader from '../components/ToolHeader'
import StickyAskBar from '../components/StickyAskBar'
import PriorityChip from '../components/PriorityChip'
import SourceTag from '../components/SourceTag'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Card, PrimaryBtn, SecondaryBtn } from '../components/JaiV2'
import UpgradePrompt from '../components/UpgradePrompt'
import { JAIMascot } from '../components/JAIMascot'
import { loadNotes, addNote, updateNote, migrateLocalNotesIfNeeded } from '../services/notesStore'
import { NOTES_PER_LEAGUE_LIMIT } from '../config/planAccess'
import { NOTE_PRIORITIES } from '../config/notePriorities'
import { formatShortDate } from '../utils/datetime'

const pinIcon = (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M9 1.5 12.5 5 8.5 9l-.5 3L4.5 8.5 1.5 10 4 7 1.5 5.5l3-.5L9 1.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
)

// Shared focus-ring treatment — the same one the old single-textarea input
// used ("already the nicest input in the app"), reused for both composer steps.
const focusRingProps = {
  onFocus: (e) => {
    e.currentTarget.style.borderColor = '#3A7CFF'
    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(58,124,255,0.12), 0 1px 4px rgba(10,31,68,0.04)'
  },
  onBlur: (e) => {
    e.currentTarget.style.borderColor = V2.hair
    e.currentTarget.style.boxShadow = '0 1px 4px rgba(10,31,68,0.04)'
  },
}
const fieldStyle = {
  width: '100%', background: 'white', border: `1px solid ${V2.hair}`,
  borderRadius: 13, padding: '13px 16px', fontSize: 13.5, color: V2.navy,
  outline: 'none', boxShadow: '0 1px 4px rgba(10,31,68,0.04)',
  fontFamily: JAI_FONTS.tight, transition: 'border-color 180ms, box-shadow 180ms',
  boxSizing: 'border-box',
}

// ── Two-step composer ────────────────────────────────────────────────────

function Composer({ leagueId, atCap, onCreated }) {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState(1)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [priority, setPriority] = useState('general')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const reset = () => {
    setOpen(false)
    setStep(1)
    setTitle('')
    setBody('')
    setPriority('general')
    setError('')
  }

  const advance = () => {
    if (!title.trim()) return
    setStep(2)
  }

  const save = async () => {
    if (!title.trim() || !body.trim() || saving) return
    setSaving(true)
    setError('')
    try {
      const created = await addNote(leagueId, body, { title: title.trim(), priority, source: 'manual' })
      onCreated(created)
      reset()
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save that note. Try again.')
    } finally {
      setSaving(false)
    }
  }

  if (atCap) {
    return (
      <V2Card pad="16px 18px" style={{ marginBottom: 10, textAlign: 'center' }}>
        <p style={{ fontSize: 13, color: V2.mute, margin: 0 }}>
          You've reached {NOTES_PER_LEAGUE_LIMIT} notes for this league — delete one to add another.
        </p>
      </V2Card>
    )
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          ...fieldStyle, textAlign: 'left', cursor: 'pointer', color: V2.faint,
          display: 'flex', alignItems: 'center', gap: 8,
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700, color: V2.blue }}>+</span>
        Jot a note — trade ideas, watch-list players, league intel…
      </button>
    )
  }

  return (
    <V2Card pad="16px 18px" style={{ marginBottom: 10 }}>
      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        {step === 2 && (
          <button
            onClick={() => setStep(1)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: V2.blue, fontSize: 12, fontWeight: 700, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            ← Back
          </button>
        )}
        <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.faint, fontFamily: JAI_FONTS.mono, marginLeft: step === 2 ? 0 : 0 }}>
          Step {step} of 2 — {step === 1 ? 'Title' : 'Details & priority'}
        </span>
        <button
          onClick={reset}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: V2.faint, fontSize: 12 }}
        >
          Cancel
        </button>
      </div>

      {step === 1 ? (
        <>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); advance() } }}
            placeholder="Give this note a title — e.g. 'Trade idea vs Rival Team'"
            autoFocus
            style={fieldStyle}
            {...focusRingProps}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <PrimaryBtn onClick={advance} disabled={!title.trim()}>Next →</PrimaryBtn>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: V2.navy, marginBottom: 10 }}>{title}</div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What do you want to remember?"
            autoFocus
            rows={4}
            style={{ ...fieldStyle, resize: 'vertical', fontFamily: JAI_FONTS.tight, lineHeight: 1.5 }}
            {...focusRingProps}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: V2.faint, letterSpacing: '0.04em', textTransform: 'uppercase', marginRight: 4 }}>Priority:</span>
            {NOTE_PRIORITIES.map((p) => (
              <button
                key={p}
                onClick={() => setPriority(p)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', opacity: priority === p ? 1 : 0.4 }}
              >
                <PriorityChip priority={p} />
              </button>
            ))}
            <PrimaryBtn onClick={save} disabled={!body.trim() || saving} style={{ marginLeft: 'auto' }}>
              {saving ? 'Saving…' : 'Save note'}
            </PrimaryBtn>
          </div>
          {error && <p style={{ fontSize: 12, color: V2.error, margin: '10px 0 0' }}>{error}</p>}
        </>
      )}
    </V2Card>
  )
}

// ── One list row ─────────────────────────────────────────────────────────

function NoteRow({ note, timezone, onTogglePin }) {
  const navigate = useNavigate()
  const { id: leagueId } = useParams()

  return (
    <V2Card
      pad="14px 18px"
      style={{ cursor: 'pointer', ...(note.pinned ? { border: '1px solid rgba(30,91,214,0.25)' } : {}) }}
      onClick={() => navigate(`/league/${leagueId}/notes/${note.id}`)}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <PriorityChip priority={note.priority} />
        <span style={{ fontSize: 15, fontWeight: 700, color: V2.navy, flex: 1, minWidth: 120 }}>{note.title}</span>
        <SourceTag source={note.source} />
        <span style={{ fontSize: 11, color: V2.faint, fontFamily: JAI_FONTS.mono, flexShrink: 0 }}>
          {formatShortDate(note.createdAt, timezone)}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onTogglePin(note) }}
          title={note.pinned ? 'Unpin' : 'Pin to top'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: note.pinned ? V2.blue : 'rgba(10,31,68,0.25)', padding: 3, display: 'flex', borderRadius: 6, flexShrink: 0 }}
        >
          {pinIcon}
        </button>
      </div>
      <div style={{ fontSize: 13, color: V2.mute, lineHeight: 1.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {note.body}
      </div>
    </V2Card>
  )
}

// ── Main ───────────────────────────────────────────────────────────────

export default function NotesPage() {
  const { id }   = useParams()
  const { user } = useAuth()
  const { leagues } = useLeagues()
  const league = leagues.find(l => l.id === Number(id))

  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')
  const [upgrade, setUpgrade] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    setUpgrade(null)
    try {
      await migrateLocalNotesIfNeeded(id)
      const serverNotes = await loadNotes(id)
      setNotes(serverNotes)
    } catch (err) {
      if (err.response?.data?.code === 'PLAN_UPGRADE_REQUIRED') {
        setUpgrade({ tool: err.response.data.requiredTool, message: err.response.data.error })
      } else {
        setLoadError("JAI couldn't load your notes right now. Try again in a minute.")
      }
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  const handleTogglePin = async (note) => {
    setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, pinned: !n.pinned } : n)))
    try {
      await updateNote(id, note.id, { pinned: !note.pinned })
    } catch {
      setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, pinned: note.pinned } : n)))
      setActionError('Could not update that note. Try again.')
    }
  }

  const sorted = useMemo(
    () => [...notes].sort((a, b) => (b.pinned - a.pinned) || (new Date(b.createdAt) - new Date(a.createdAt))),
    [notes]
  )

  const used = notes.length
  const atCap = used >= NOTES_PER_LEAGUE_LIMIT

  const askBarContext = useMemo(() => {
    if (!sorted.length) return undefined
    return `THE USER'S SAVED NOTES FOR THIS LEAGUE:\n${sorted.map((n) => `- [${n.priority}] ${n.title}: ${n.body}`).join('\n')}`
  }, [sorted])

  return (
    <AppShell>
      <div style={{ padding: '30px 40px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 720, margin: '0 auto', paddingBottom: 100 }}>
          <ToolHeader
            leagueName={league?.name}
            title="My Notes"
            subtitle="Strategy notes only you can see. JAI reads them for context when you ask questions."
            back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
          />

          {upgrade ? (
            <UpgradePrompt tool={upgrade.tool} message={upgrade.message} />
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <span style={{ fontSize: 11.5, color: V2.faint, fontFamily: JAI_FONTS.mono }}>
                  {used} / {NOTES_PER_LEAGUE_LIMIT} notes
                </span>
              </div>

              <Composer leagueId={id} atCap={atCap} onCreated={(note) => setNotes((prev) => [note, ...prev])} />

              {actionError && <p style={{ fontSize: 12.5, color: V2.error, margin: '0 0 12px' }}>{actionError}</p>}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {loading && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 0', gap: 12 }}>
                    <JAIMascot size={48} state="thinking" animate={true} />
                    <p style={{ fontSize: 13, color: V2.faint, margin: 0 }}>Loading your notes…</p>
                  </div>
                )}

                {!loading && loadError && (
                  <V2Card pad="22px 20px" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 13.5, color: V2.error, marginBottom: 8 }}>{loadError}</div>
                    <SecondaryBtn onClick={load}>Try again</SecondaryBtn>
                  </V2Card>
                )}

                {!loading && !loadError && sorted.length === 0 && (
                  <V2Card pad="22px 20px" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 13.5, color: V2.mute }}>
                      No notes yet. Trade ideas, waiver watch-lists, injury reminders — anything you want to remember.
                    </div>
                  </V2Card>
                )}

                {!loading && !loadError && sorted.map((n) => (
                  <NoteRow key={n.id} note={n} timezone={user?.timezone} onTogglePin={handleTogglePin} />
                ))}
              </div>
            </>
          )}

          <p style={{ textAlign: 'center', fontSize: 11.5, color: V2.faint, marginTop: 16 }}>
            Notes sync to your account — sign in anywhere to see them.
          </p>
        </div>
      </div>

      <StickyAskBar
        leagueId={id}
        scope="notes"
        context={askBarContext}
        placeholder="Ask JAI about your notes"
      />
    </AppShell>
  )
}
